/**
 * User-level preferences for AutoCAD-parity preview highlighting.
 *
 * Mirrors the four AutoCAD system variables that govern rollover
 * highlighting behaviour:
 *   • SELECTIONPREVIEW — whose-selection: 0=none, 1=when-no-cmd,
 *     2=during-cmd, 3=both. Controls whether a visual preview is
 *     emitted at all.
 *   • PREVIEWEFFECT    — emphasis style: 0=dash, 1=thicken, 2=both.
 *   • PREVIEWCOLOR     — hex colour used for the preview stroke.
 *     Stored as "#RRGGBB" so the renderer can feed it directly to
 *     Konva / CSS without re-converting from ACI.
 *   • PREVIEWFILTER    — six-bit mask that excludes certain entity
 *     categories from rollover preview (locked-layer, xrefs, tables,
 *     mlines, hatches, groups).
 *
 * Persistence: localStorage key `edit-dwg.preview`. Reads are wrapped
 * in try/catch so SSR / private-browsing mode falls back to defaults
 * — same pattern as i18n/index.ts.
 */

import { create } from "zustand";

export type SelectionPreviewMode = 0 | 1 | 2 | 3;
export type PreviewEffect = 0 | 1 | 2;

export const PREVIEW_FILTER_LOCKED_LAYER = 1;
export const PREVIEW_FILTER_XREFS = 2;
export const PREVIEW_FILTER_TABLES = 4;
export const PREVIEW_FILTER_MLINES = 8;
export const PREVIEW_FILTER_HATCHES = 16;
export const PREVIEW_FILTER_GROUPS = 32;

export const PREVIEW_FILTER_MAX = 63;

/**
 * AutoCAD ROLLOVERTIPS — controls whether the property tooltip card
 * appears when hovering over an entity (with no active command). 0 =
 * suppressed, 1 = shown. AutoCAD default = 1 (verified via MCP
 * `get_variables`).
 */
export type RolloverTips = 0 | 1;

export interface PreviewPreferences {
  selectionPreview: SelectionPreviewMode;
  previewEffect: PreviewEffect;
  previewColor: string;
  previewFilter: number;
  rolloverTips: RolloverTips;
}

export const DEFAULT_PREVIEW_PREFERENCES: PreviewPreferences = {
  selectionPreview: 3,
  // AutoCAD parity (verified via MCP `get_variables` 2026-04 on a fresh
  // 2025 install): PREVIEWEFFECT default is 0 — dashed-thickened outline
  // (cheap, no HW gate). 1 = glowing outline + solid face fill (HW
  // accelerated; deferred — we render outline-only when 1 is selected
  // and emit a console warning).
  previewEffect: 0,
  previewColor: "#8ec5ff",
  previewFilter:
    PREVIEW_FILTER_LOCKED_LAYER | PREVIEW_FILTER_XREFS | PREVIEW_FILTER_TABLES,
  rolloverTips: 1,
};

const LOCAL_STORAGE_KEY = "edit-dwg.preview";

const HEX_6_PATTERN = /^#?[0-9a-fA-F]{6}$/;
const HEX_3_PATTERN = /^#?[0-9a-fA-F]{3}$/;

export function isValidHexColor(color: string): boolean {
  if (typeof color !== "string") return false;
  return HEX_6_PATTERN.test(color) || HEX_3_PATTERN.test(color);
}

export function normalizeHexColor(color: string): string {
  if (!isValidHexColor(color)) {
    return DEFAULT_PREVIEW_PREFERENCES.previewColor;
  }
  const stripped = color.startsWith("#") ? color.slice(1) : color;
  const expanded =
    stripped.length === 3
      ? stripped
          .split("")
          .map((ch) => ch + ch)
          .join("")
      : stripped;
  return `#${expanded.toLowerCase()}`;
}

function isSelectionPreviewMode(value: unknown): value is SelectionPreviewMode {
  return value === 0 || value === 1 || value === 2 || value === 3;
}

function isPreviewEffect(value: unknown): value is PreviewEffect {
  return value === 0 || value === 1 || value === 2;
}

function isValidFilter(value: unknown): value is number {
  return (
    typeof value === "number" &&
    Number.isInteger(value) &&
    value >= 0 &&
    value <= PREVIEW_FILTER_MAX
  );
}

function isRolloverTips(value: unknown): value is RolloverTips {
  return value === 0 || value === 1;
}

export function readPersistedPreviewPreferences(): PreviewPreferences {
  if (typeof window === "undefined") {
    return { ...DEFAULT_PREVIEW_PREFERENCES };
  }
  try {
    const stored = window.localStorage.getItem(LOCAL_STORAGE_KEY);
    if (!stored) return { ...DEFAULT_PREVIEW_PREFERENCES };
    const parsed: unknown = JSON.parse(stored);
    if (!parsed || typeof parsed !== "object") {
      return { ...DEFAULT_PREVIEW_PREFERENCES };
    }
    const candidate = parsed as Partial<PreviewPreferences>;
    return {
      selectionPreview: isSelectionPreviewMode(candidate.selectionPreview)
        ? candidate.selectionPreview
        : DEFAULT_PREVIEW_PREFERENCES.selectionPreview,
      previewEffect: isPreviewEffect(candidate.previewEffect)
        ? candidate.previewEffect
        : DEFAULT_PREVIEW_PREFERENCES.previewEffect,
      previewColor:
        typeof candidate.previewColor === "string" &&
        isValidHexColor(candidate.previewColor)
          ? normalizeHexColor(candidate.previewColor)
          : DEFAULT_PREVIEW_PREFERENCES.previewColor,
      previewFilter: isValidFilter(candidate.previewFilter)
        ? candidate.previewFilter
        : DEFAULT_PREVIEW_PREFERENCES.previewFilter,
      rolloverTips: isRolloverTips(candidate.rolloverTips)
        ? candidate.rolloverTips
        : DEFAULT_PREVIEW_PREFERENCES.rolloverTips,
    };
  } catch {
    return { ...DEFAULT_PREVIEW_PREFERENCES };
  }
}

export function persistPreviewPreferences(prefs: PreviewPreferences): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // localStorage may be unavailable — ignore.
  }
}

type PreviewPreferencesStore = PreviewPreferences & {
  setSelectionPreview: (mode: SelectionPreviewMode) => void;
  setPreviewEffect: (effect: PreviewEffect) => void;
  setPreviewColor: (color: string) => void;
  setPreviewFilter: (filter: number) => void;
  togglePreviewFilterBit: (bit: number) => void;
  setRolloverTips: (value: RolloverTips) => void;
  reset: () => void;
};

const usePreviewPreferencesStore = create<PreviewPreferencesStore>(
  (set, get) => ({
    ...readPersistedPreviewPreferences(),
    setSelectionPreview: (mode) => {
      const next = { ...currentPrefs(get()), selectionPreview: mode };
      persistPreviewPreferences(next);
      set({ selectionPreview: mode });
    },
    setPreviewEffect: (effect) => {
      const next = { ...currentPrefs(get()), previewEffect: effect };
      persistPreviewPreferences(next);
      set({ previewEffect: effect });
    },
    setPreviewColor: (color) => {
      const normalized = normalizeHexColor(color);
      const next = { ...currentPrefs(get()), previewColor: normalized };
      persistPreviewPreferences(next);
      set({ previewColor: normalized });
    },
    setPreviewFilter: (filter) => {
      const clamped = isValidFilter(filter)
        ? filter
        : DEFAULT_PREVIEW_PREFERENCES.previewFilter;
      const next = { ...currentPrefs(get()), previewFilter: clamped };
      persistPreviewPreferences(next);
      set({ previewFilter: clamped });
    },
    togglePreviewFilterBit: (bit) => {
      if (!isValidFilter(bit) || bit === 0) return;
      const current = get().previewFilter;
      const toggled = (current ^ bit) & PREVIEW_FILTER_MAX;
      const next = { ...currentPrefs(get()), previewFilter: toggled };
      persistPreviewPreferences(next);
      set({ previewFilter: toggled });
    },
    setRolloverTips: (value) => {
      const next = { ...currentPrefs(get()), rolloverTips: value };
      persistPreviewPreferences(next);
      set({ rolloverTips: value });
    },
    reset: () => {
      persistPreviewPreferences(DEFAULT_PREVIEW_PREFERENCES);
      set({ ...DEFAULT_PREVIEW_PREFERENCES });
    },
  }),
);

function currentPrefs(store: PreviewPreferencesStore): PreviewPreferences {
  return {
    selectionPreview: store.selectionPreview,
    previewEffect: store.previewEffect,
    previewColor: store.previewColor,
    previewFilter: store.previewFilter,
    rolloverTips: store.rolloverTips,
  };
}

/**
 * React hook exposing the preview-preferences store plus its setters.
 * Components that render the Options dialog consume this directly.
 */
export function usePreviewPreferences(): PreviewPreferencesStore {
  return usePreviewPreferencesStore();
}

/**
 * Pure getter for non-React callers (reducers, hit-test, renderer
 * helpers). Matches the i18n `t()` pattern.
 * @public
 */
export function getPreviewPreferences(): PreviewPreferences {
  const state = usePreviewPreferencesStore.getState();
  return currentPrefs(state);
}

/**
 * Snapshot getter for setters (non-React callers — sysvar bridge,
 * tests). Returns the live `PreviewPreferencesStore` API so callers
 * can invoke `setPreviewEffect(0)` etc. without going through React.
 */
export function getPreviewPreferencesApi(): PreviewPreferencesStore {
  return usePreviewPreferencesStore.getState();
}
